import React, { useEffect, useRef, useState } from "react";
import { Stepper } from "primereact/stepper";
import { StepperPanel } from "primereact/stepperpanel";
import REIButton from "./button";

interface IStep {
  header: string;
  content: React.ReactNode;
  validate?: () => boolean;
}

interface IREIStepperProps {
  id?: string;
  steps: IStep[];
  linear?: boolean;
  centertext?: string;
  allowStepClick?: boolean;
}

const REIStepper: React.FC<IREIStepperProps> = ({ id, steps, linear, centertext, allowStepClick = false }) => {
  const stepperRef = useRef<any>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [validSteps, setValidSteps] = useState(Array(steps.length).fill(false));
  const [errorMessage, setErrorMessage] = useState("");
  const isTopStepNavigationEnabled = allowStepClick;
  const isLinearStepper = isTopStepNavigationEnabled ? false : (linear ?? true);

  const validateStep = (stepIndex: number) => {
    return steps[stepIndex]?.validate ? steps[stepIndex].validate() : true;
  };

  const markStepAsValid = (stepIndex: number) => {
    setValidSteps((prevValidSteps) => {
      if (prevValidSteps[stepIndex]) {
        return prevValidSteps;
      }

      const nextValidSteps = [...prevValidSteps];
      nextValidSteps[stepIndex] = true;
      return nextValidSteps;
    });
  };

  const syncActiveStep = (stepIndex: number) => {
    setActiveStep(stepIndex);
    stepperRef.current?.setActiveStep(stepIndex);
  };

  const handleStepNavigation = (stepIndex: number) => {
    if (stepIndex === activeStep) {
      return;
    }

    if (isTopStepNavigationEnabled) {
      setErrorMessage("");
      syncActiveStep(stepIndex);
      return;
    }

    if (!isLinearStepper) {
      setErrorMessage("");
      syncActiveStep(stepIndex);
      return;
    }

    if (stepIndex < activeStep) {
      setErrorMessage("");
      syncActiveStep(stepIndex);
      return;
    }

    if (stepIndex === activeStep + 1 && validateStep(activeStep)) {
      markStepAsValid(activeStep);
      setErrorMessage("");
      syncActiveStep(stepIndex);
      return;
    }

    if (validSteps.slice(0, stepIndex).every(Boolean)) {
      setErrorMessage("");
      syncActiveStep(stepIndex);
      return;
    }

    setErrorMessage("Please complete the required fields before proceeding.");
  };

  const goToNextStep = () => {
    if (validateStep(activeStep)) {
      markStepAsValid(activeStep);
      setErrorMessage("");
      syncActiveStep(activeStep + 1);
    } else {
      setErrorMessage("Please complete the required fields before proceeding.");
    }
  };

  const goToPreviousStep = () => {
    syncActiveStep(activeStep - 1);
    setErrorMessage("");
  };

  useEffect(() => {
    if (!isTopStepNavigationEnabled || !stepperRef.current?.getElement) {
      return;
    }

    const stepperElement = stepperRef.current.getElement() as HTMLElement | undefined;

    if (!stepperElement) {
      return;
    }

    const stepButtons = Array.from(stepperElement.querySelectorAll<HTMLButtonElement>(".p-stepper-action"));

    const listeners = stepButtons.map((button, index) => {
      const handleClick = (event: Event) => {
        event.preventDefault();
        event.stopPropagation();
        handleStepNavigation(index);
      };

      button.addEventListener("click", handleClick, true);

      return { button, handleClick };
    });

    return () => {
      listeners.forEach(({ button, handleClick }) => {
        button.removeEventListener("click", handleClick, true);
      });
    };
  }, [activeStep, isLinearStepper, isTopStepNavigationEnabled, validSteps]);

  return (
    <Stepper ref={stepperRef} activeStep={activeStep} linear={isLinearStepper}>
      {steps.map((step, index) => (
        <StepperPanel key={index} header={step.header}>
          <>{step.content}</>
          {errorMessage && activeStep === index && (
            <div className="pt-2" style={{ color: "red" }}>
              {errorMessage}
            </div>
          )}
          <div className="flex pt-4 justify-content-between">
            <div className="flex-1 w-1/3 text-start">
              {index > 0 && (
                <REIButton
                  label="Back"
                  id={id ?? `REIButton-Back-${index}`}
                  testId={id ?? `REIButton-Back-${index}`}
                  severity="secondary"
                  icon="pi pi-arrow-left"
                  onClick={goToPreviousStep}
                />
              )}
            </div>
            <div className="flex-1 w-1/3 text-center">{centertext}</div>
            <div className="flex-1 w-1/3  text-end">
              {index < steps.length - 1 && (
                <REIButton
                  label="Next"
                  id={id ?? `REIButton-Next-${index}`}
                  testId={id ?? `REIButton-Next-${index}`}
                  icon="pi pi-arrow-right"
                  iconPos="right"
                  onClick={goToNextStep}
                />
              )}
            </div>
          </div>
        </StepperPanel>
      ))}
    </Stepper>
  );
};

export default REIStepper;
